/**
 * Catering role-based access — pure rules (no React, no Redux).
 * The flags come from the employee's CATERING permissions (`services/cateringAccessApi.ts`).
 */
import type { CateringAccess } from '../../../services/cateringAccessPermissions';
import { cateringPaths } from '../paths';

/**
 * A part of Catering a page belongs to:
 *  - bookings — Leads, Estimates, Orders (incl. documents, recurring), Invoices, Email/BEO templates;
 *  - customers, packages, settings (the Catering Settings page only);
 *  - any — the Catering hub (open with access to any part).
 */
export type CateringArea = 'bookings' | 'customers' | 'packages' | 'settings' | 'any';

/** Can the employee OPEN pages of this area? */
export function canOpenArea(access: CateringAccess, area: CateringArea): boolean {
  switch (area) {
    case 'bookings': return access.viewBookings;
    case 'customers': return access.viewCustomers;
    case 'packages': return access.viewPackages;
    case 'settings': return access.settings;
    case 'any': return firstCateringPath(access) !== null;
    default: return false;
  }
}

/** Can the employee CHANGE things in this area? (Customers: tags are the only edit, so View Customers can change them; Settings is one View/Edit permission.) */
export function canEditArea(access: CateringAccess, area: CateringArea): boolean {
  switch (area) {
    case 'bookings': return access.editBookings;
    case 'packages': return access.editPackages;
    case 'settings': return access.settings;
    case 'customers': return access.viewCustomers;
    default: return false;
  }
}

/** The Catering sub-pages in side-nav order, with the area each needs. */
const NAV_ORDER: { path: string; area: Exclude<CateringArea, 'any'> }[] = [
  { path: cateringPaths.leads, area: 'bookings' },
  { path: cateringPaths.estimates, area: 'bookings' },
  { path: cateringPaths.orders, area: 'bookings' },
  { path: cateringPaths.invoices, area: 'bookings' },
  { path: cateringPaths.customers, area: 'customers' },
  { path: cateringPaths.packages, area: 'packages' },
  { path: cateringPaths.settings, area: 'settings' },
];

/** Where the Catering parent item lands: the first sub-page (in nav order) the employee can open, or `null` for none. */
export function firstCateringPath(access: CateringAccess): string | null {
  return NAV_ORDER.find((entry) => canOpenArea(access, entry.area))?.path ?? null;
}

/** Shown when an action needs a permission the employee doesn't have. */
export const NO_PERMISSION_MESSAGE = "You don't have permission";
